// src/components/Admin/FacilityManagementEnhanced.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Avatar,
  styled,
  LinearProgress,
  Grid,
  alpha,
  InputAdornment,
  Menu,
  MenuItem as MuiMenuItem,
  Divider,
  Stack,
  Autocomplete,
  Paper,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Visibility,
  Search,
  Business,
  CheckCircle,
  Cancel,
  MoreVert,
  Refresh,
  Person,
  Email,
  Phone,
  LocationOn,
  Close,
  ArrowDropDown,
  CloudUpload,
  Image as ImageIcon,
  Dashboard as DashboardIcon,
  KeyboardArrowDown,
  KeyboardArrowUp,
  Description,
  SettingsEthernet,
  LocalHospital,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import {
  getFacilities,
  createFacility,
  updateFacility,
  deleteFacility,
  updateFacilityStatus,
  uploadFacilityLogo,
  getFacilityLogo,
  deleteFacilityLogo,
  getInHouseRadiologists,
  assignInHouseRadiologist,
} from '../../services/facilities';

import { getScanBoardStudies } from '../../services/pacs';
import { getStudyStatistics } from '../../services/analytics';
import ReportStatusBadge from '../Common/ReportStatusBadge';
import ReportViewer from '../Common/ReportViewer';
import FacilityIntegrationDialog from './FacilityIntegrationDialog';
import { useThemeMode } from '../../contexts/ThemeModeContext';
import { 
  Collapse, 
  TablePagination,
  List as MuiList,
  ListItem,
  ListItemText as MuiListItemText,
  CircularProgress,
} from '@mui/material';

const teslaColors = {
  primary: '#3E6AE1',
  secondary: '#34C3FF',
  success: '#4CAF50',
  warning: '#ff9800',
  error: '#f44336',
  info: '#2196F3',
};

const GlassCard = styled(Card)(({ theme }) => ({
  background: theme.palette.mode === 'dark'
    ? 'rgba(30, 30, 30, 0.8)'
    : 'rgba(255, 255, 255, 0.8)',
  backdropFilter: 'blur(10px)',
  borderRadius: '16px',
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: theme.palette.mode === 'dark'
    ? '0 8px 32px 0 rgba(0, 0, 0, 0.36)'
    : '0 8px 32px 0 rgba(0, 0, 0, 0.1)',
}));

const GradientButton = styled(Button)(({ theme }) => ({
  background: `linear-gradient(45deg, ${teslaColors.primary}, ${teslaColors.secondary})`,
  color: 'white',
  '&:hover': {
    background: `linear-gradient(45deg, ${teslaColors.secondary}, ${teslaColors.primary})`,
  },
}));

const StatsBox = ({ icon, label, value, color }) => (
  <Box
    sx={{
      p: 1.25,
      borderRadius: '10px',
      background: alpha(color, 0.1),
      display: 'flex',
      alignItems: 'center',
      gap: 1.5,
      transition: 'transform 0.2s, box-shadow 0.2s',
      '&:hover': {
        transform: 'translateY(-1px)',
        boxShadow: 1,
      },
    }}
  >
    <Box
      sx={{
        width: 36,
        height: 36,
        borderRadius: '8px',
        background: `linear-gradient(45deg, ${color}, ${alpha(color, 0.7)})`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      {React.cloneElement(icon, { sx: { color: 'white', fontSize: 20 } })}
    </Box>
    <Box>
      <Typography sx={{ fontFamily: '"Satoshi", sans-serif', fontWeight: 700, fontSize: '1.15rem', lineHeight: 1.2 }}>
        {value}
      </Typography>
      <Typography variant="body2" sx={{ fontFamily: '"Satoshi", sans-serif', fontSize: '0.75rem', color: 'text.secondary' }}>
        {label}
      </Typography>
    </Box>
  </Box>
);

const StatusChip = ({ status }) => {
  const getStatusConfig = (status) => {
    switch (status) {
      case 'active':
        return { label: 'Active', color: teslaColors.success };
      case 'inactive':
        return { label: 'Inactive', color: teslaColors.error };
      case 'pending':
        return { label: 'Pending', color: teslaColors.warning };
      case 'suspended':
        return { label: 'Suspended', color: teslaColors.error };
      default:
        return { label: 'Unknown', color: teslaColors.primary };
    }
  };

  const config = getStatusConfig(status);

  return (
    <Chip
      label={config.label}
      size="small"
      sx={{
        background: alpha(config.color, 0.1),
        color: config.color,
        fontWeight: 600,
        textTransform: 'capitalize',
        border: `1px solid ${alpha(config.color, 0.3)}`,
      }}
    />
  );
};

function FacilityManagement() {
  const navigate = useNavigate();
  const { mode } = useThemeMode();
  const isDark = mode === 'dark';
  const cardBg = isDark ? '#1E1E1E' : '#FFFFFF';
  const borderColor = isDark ? 'rgba(255, 255, 255, 0.08)' : '#E2E8F0';
  const textColorPrimary = isDark ? '#F1F5F9' : '#0F172A';
  const textColorSecondary = isDark ? '#CBD5E1' : '#475569';
  const [facilities, setFacilities] = useState([]);
  const [expandedFacility, setExpandedFacility] = useState(null);
  const [facilityStudies, setFacilityStudies] = useState([]);
  const [studiesLoading, setStudiesLoading] = useState(false);
  const [studiesPage, setStudiesPage] = useState(0);
  const [studiesRowsPerPage, setStudiesRowsPerPage] = useState(5);
  const [totalStudies, setTotalStudies] = useState(0);
  const [filteredFacilities, setFilteredFacilities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedFacility, setSelectedFacility] = useState(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [integrationDialogOpen, setIntegrationDialogOpen] = useState(false);
  const [reportsDialogOpen, setReportsDialogOpen] = useState(false);
  const [facilityReportsStudies, setFacilityReportsStudies] = useState([]);
  const [facilityReportsLoading, setFacilityReportsLoading] = useState(false);
  const [selectedStudyForReport, setSelectedStudyForReport] = useState(null);
  const [reportViewerDialogOpen, setReportViewerDialogOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [statusMenuAnchorEl, setStatusMenuAnchorEl] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [logoUploading, setLogoUploading] = useState(false);
  const [inHouseRadiologists, setInHouseRadiologists] = useState([]);
  const [assignRadiologistDialogOpen, setAssignRadiologistDialogOpen] = useState(false);
  const [assigningRadiologist, setAssigningRadiologist] = useState(false);
  const [selectedRadId, setSelectedRadId] = useState('');
  const [autoAssignToggle, setAutoAssignToggle] = useState(true);

  // Form state for add/edit
  const [formData, setFormData] = useState({
    facilityName: '',
    facilityCode: '',
    facilityId: '',
    alternateNames: [''],
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'United States',
    phoneNumber: '',
    email: '',
    contactPerson: {
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: '',
    },
    status: 'pending',
    inHouseRadiologist: '',
    autoAssignToInHouse: true,
  });

  // Generate facility ID
  const generateFacilityId = useCallback(() => {
    const prefix = 'DYC';
    const randomNum = Math.floor(Math.random() * 9000) + 1000;
    return `${prefix}-${randomNum}`;
  }, []);

  const fetchFacilities = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      console.log('Fetching facilities...');
      const response = await getFacilities({ limit: 1000 });
      console.log('Facilities API response:', response);

      let facilitiesList = [];
      
      if (Array.isArray(response)) {
        facilitiesList = response;
      } else if (response?.facilities && Array.isArray(response.facilities)) {
        facilitiesList = response.facilities;
      } else if (response?.data?.facilities && Array.isArray(response.data.facilities)) {
        facilitiesList = response.data.facilities;
      } else if (response?.data && Array.isArray(response.data)) {
        facilitiesList = response.data;
      } else {
        console.warn('Unexpected response structure:', response);
        facilitiesList = [];
      }

      console.log('Processed facilities list:', facilitiesList);
      setFacilities(facilitiesList);
      setFilteredFacilities(facilitiesList);
    } catch (err) {
      console.error('Error fetching facilities:', err);
      setError('Failed to fetch facilities: ' + (err.message || 'Unknown error'));
      setFacilities([]);
      setFilteredFacilities([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchInHouseRadiologists = useCallback(async () => {
    try {
      const response = await getInHouseRadiologists();
      if (response?.radiologists) {
        setInHouseRadiologists(response.radiologists);
      }
    } catch (err) {
      console.warn('Could not fetch in-house radiologists:', err);
    }
  }, []);

  useEffect(() => {
    fetchFacilities();
    fetchInHouseRadiologists();
  }, [fetchFacilities, fetchInHouseRadiologists]);

  // Filter facilities whenever search term or filters change
  useEffect(() => {
    if (!Array.isArray(facilities)) {
      console.error('Facilities is not an array:', facilities);
      setFilteredFacilities([]);
      return;
    }

    let filtered = [...facilities];

    // Search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (facility) =>
          facility.facilityName?.toLowerCase().includes(searchLower) ||
          facility.facilityCode?.toLowerCase().includes(searchLower) ||
          facility.facilityId?.toLowerCase().includes(searchLower) ||
          facility.city?.toLowerCase().includes(searchLower) ||
          facility.contactPerson?.email?.toLowerCase().includes(searchLower) ||
          facility.email?.toLowerCase().includes(searchLower) ||
          (Array.isArray(facility.alternateNames) && 
            facility.alternateNames.some(name => 
              name?.toLowerCase().includes(searchLower)
            ))
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter((facility) => facility.status === statusFilter);
    }

    setFilteredFacilities(filtered);
  }, [searchTerm, statusFilter, facilities]);

  const handleAddFacility = async () => {
    setActionLoading(true);
    setError('');
    try {
      // Filter out empty alternate names
      const cleanedData = {
        ...formData,
        facilityId: formData.facilityId || generateFacilityId(),
        alternateNames: formData.alternateNames.filter(name => name.trim() !== ''),
      };

      const result = await createFacility(cleanedData);
      setSuccess('Facility created successfully');
      setAddDialogOpen(false);
      resetForm();
      fetchFacilities();

      // Guide the admin straight to the DICOM/HL7 connection details for the
      // facility they just created — no need to hunt through the action menu.
      if (result?.facility) {
        setSelectedFacility(result.facility);
        setIntegrationDialogOpen(true);
      }
    } catch (err) {
      setError(err.message || 'Failed to create facility');
    } finally {
      setActionLoading(false);
    }
  };

  const handleEditClick = async (facility) => {
    setSelectedFacility(facility);
    setFormData({
      facilityName: facility.facilityName || '',
      facilityCode: facility.facilityCode || '',
      facilityId: facility.facilityId || generateFacilityId(),
      alternateNames: Array.isArray(facility.alternateNames) ? facility.alternateNames : [''],
      address: facility.address || '',
      city: facility.city || '',
      state: facility.state || '',
      zipCode: facility.zipCode || '',
      country: facility.country || 'United States',
      phoneNumber: facility.phoneNumber || '',
      email: facility.email || '',
      contactPerson: {
        firstName: facility.contactPerson?.firstName || '',
        lastName: facility.contactPerson?.lastName || '',
        email: facility.contactPerson?.email || '',
        phoneNumber: facility.contactPerson?.phoneNumber || '',
      },
      status: facility.status || 'pending',
      inHouseRadiologist: facility.inHouseRadiologist?._id || facility.inHouseRadiologist || '',
      autoAssignToInHouse: facility.autoAssignToInHouse !== false,
    });
    
    // Load facility logo if exists
    setLogoPreview(null);
    setLogoFile(null);
    if (facility.logo?.data || facility.logo) {
      try {
        const logoResponse = await getFacilityLogo(facility._id);
        if (logoResponse?.logo?.dataUrl) {
          setLogoPreview(logoResponse.logo.dataUrl);
        }
      } catch (err) {
        console.warn('Could not load facility logo:', err);
      }
    }
    
    setEditDialogOpen(true);
    handleMenuClose();
  };

  const handleUpdateFacility = async () => {
    if (!selectedFacility) return;
    
    setActionLoading(true);
    setError('');
    try {
      const cleanedData = {
        ...formData,
        alternateNames: formData.alternateNames.filter(name => name.trim() !== ''),
      };
      
      await updateFacility(selectedFacility._id, cleanedData);
      
      // Upload logo if a new one was selected
      if (logoFile) {
        setLogoUploading(true);
        try {
          await uploadFacilityLogo(selectedFacility._id, logoFile);
        } catch (logoErr) {
          console.error('Logo upload error:', logoErr);
          // Don't fail the whole update if logo upload fails
        } finally {
          setLogoUploading(false);
        }
      }
      
      setSuccess('Facility updated successfully');
      setEditDialogOpen(false);
      resetForm();
      setLogoPreview(null);
      setLogoFile(null);
      fetchFacilities();
      fetchInHouseRadiologists();
    } catch (err) {
      setError(err.message || 'Failed to update facility');
    } finally {
      setActionLoading(false);
    }
  };

  const handleLogoSelect = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/webp', 'image/svg+xml'];
      if (!validTypes.includes(file.type)) {
        setError('Invalid file type. Please select a JPEG, PNG, WebP, or SVG image.');
        return;
      }
      
      // Validate file size (2MB max)
      if (file.size > 2 * 1024 * 1024) {
        setError('File size too large. Please select an image smaller than 2MB.');
        return;
      }
      
      setLogoFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleLogoDelete = async () => {
    if (!selectedFacility) return;
    
    try {
      await deleteFacilityLogo(selectedFacility._id);
      setLogoPreview(null);
      setLogoFile(null);
      setSuccess('Logo deleted successfully');
      fetchFacilities();
    } catch (err) {
      setError(err.message || 'Failed to delete logo');
    }
  };

  const handleViewClick = (facility) => {
    setSelectedFacility(facility);
    setViewDialogOpen(true);
    handleMenuClose();
  };

  const handleIntegrationClick = (facility) => {
    setSelectedFacility(facility);
    setIntegrationDialogOpen(true);
    handleMenuClose();
  };

  const handleViewReports = async (facility) => {
    setSelectedFacility(facility);
    setReportsDialogOpen(true);
    setFacilityReportsLoading(true);
    setFacilityReportsStudies([]);
    
    try {
      // Fetch all studies for this facility
      const params = {
        facility: facility._id,
        limit: 1000, // Get all studies
        sort: '-createdAt'
      };
      const response = await getScanBoardStudies(params);
      setFacilityReportsStudies(response.studies || []);
    } catch (err) {
      console.error('Error fetching facility reports:', err);
      setError('Failed to load reports for this facility');
    } finally {
      setFacilityReportsLoading(false);
    }
    handleMenuClose();
  };

  const handleViewStudyReport = (study) => {
    setSelectedStudyForReport(study);
    setReportViewerDialogOpen(true);
  };

  const handleViewFacilityImages = (facility) => {
    if (!facility) return;
    
    if (expandedFacility === facility._id) {
      setExpandedFacility(null);
      setFacilityStudies([]);
    } else {
      setExpandedFacility(facility._id);
      setStudiesPage(0);
      fetchFacilityStudies(facility._id, 0, studiesRowsPerPage);
    }
    handleMenuClose();
  };

  const fetchFacilityStudies = async (facilityId, page, rowsPerPage) => {
    setStudiesLoading(true);
    try {
      const params = {
        facility: facilityId,
        limit: rowsPerPage,
        offset: page * rowsPerPage,
        sort: '-createdAt'
      };
      const response = await getScanBoardStudies(params);
      setFacilityStudies(response.studies || []);
      setTotalStudies(response.totalStudies || 0);
    } catch (err) {
      console.error('Error fetching facility studies:', err);
      setError('Failed to load studies for this facility');
    } finally {
      setStudiesLoading(false);
    }
  };

  const handleStudiesPageChange = (event, newPage) => {
    setStudiesPage(newPage);
    fetchFacilityStudies(expandedFacility, newPage, studiesRowsPerPage);
  };

  const handleStudiesRowsPerPageChange = (event) => {
    const newRows = parseInt(event.target.value, 10);
    setStudiesRowsPerPage(newRows);
    setStudiesPage(0);
    fetchFacilityStudies(expandedFacility, 0, newRows);
  };

  const handleDeleteClick = (facility) => {
    setSelectedFacility(facility);
    setDeleteDialogOpen(true);
    setDeleteError('');
    handleMenuClose();
  };

  const handleDeleteConfirm = async () => {
    if (!selectedFacility) return;

    setActionLoading(true);
    setDeleteError('');
    try {
      await deleteFacility(selectedFacility._id);
      setSuccess('Facility deleted successfully');
      setDeleteDialogOpen(false);
      setSelectedFacility(null);
      fetchFacilities();
    } catch (err) {
      setDeleteError(err.message || 'Failed to delete facility');
    } finally {
      setActionLoading(false);
    }
  };

  const handleStatusChange = async (facilityId, newStatus) => {
    try {
      setError('');
      await updateFacilityStatus(facilityId, newStatus);
      setSuccess('Facility status updated successfully');
      fetchFacilities();
    } catch (err) {
      setError(err.message || 'Failed to update status');
    }
  };

  const handleMenuOpen = (event, facility) => {
    setAnchorEl(event.currentTarget);
    setSelectedFacility(facility);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setStatusMenuAnchorEl(null);
  };

  const handleStatusMenuOpen = (event) => {
    event.stopPropagation();
    setStatusMenuAnchorEl(event.currentTarget);
  };

  const handleStatusMenuClose = () => {
    setStatusMenuAnchorEl(null);
  };

  const handleQuickStatusChange = async (newStatus) => {
    if (!selectedFacility) return;
    handleStatusMenuClose();
    await handleStatusChange(selectedFacility._id, newStatus);
    handleMenuClose();
  };

  const resetForm = () => {
    setFormData({
      facilityName: '',
      facilityCode: '',
      facilityId: generateFacilityId(),
      alternateNames: [''],
      address: '',
      city: '',
      state: '',
      zipCode: '',
      country: 'United States',
      phoneNumber: '',
      email: '',
      contactPerson: {
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
      },
      status: 'pending',
      inHouseRadiologist: '',
      autoAssignToInHouse: true,
    });
    setSelectedFacility(null);
    setError('');
    setLogoPreview(null);
    setLogoFile(null);
  };

  const handleOpenAssignRadiologistDialog = (facility) => {
    setSelectedFacility(facility);
    setSelectedRadId(facility.inHouseRadiologist?._id || facility.inHouseRadiologist || '');
    setAutoAssignToggle(facility.autoAssignToInHouse !== false);
    setAssignRadiologistDialogOpen(true);
    handleMenuClose();
  };

  const handleSaveAssignRadiologist = async () => {
    if (!selectedFacility) return;
    setAssigningRadiologist(true);
    setError('');
    try {
      await assignInHouseRadiologist(selectedFacility._id, {
        inHouseRadiologistId: selectedRadId || null,
        autoAssignToInHouse: autoAssignToggle
      });
      setSuccess('In-house radiologist assignment updated successfully');
      setAssignRadiologistDialogOpen(false);
      fetchFacilities();
      fetchInHouseRadiologists();
    } catch (err) {
      setError(err.message || 'Failed to assign in-house radiologist');
    } finally {
      setAssigningRadiologist(false);
    }
  };

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleContactPersonChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      contactPerson: {
        ...prev.contactPerson,
        [field]: value,
      },
    }));
  };

  const handleAlternateNameChange = (index, value) => {
    const newAlternateNames = [...formData.alternateNames];
    newAlternateNames[index] = value;
    setFormData({ ...formData, alternateNames: newAlternateNames });
  };

  const addAlternateName = () => {
    setFormData({
      ...formData,
      alternateNames: [...formData.alternateNames, ''],
    });
  };

  const removeAlternateName = (index) => {
    const newAlternateNames = formData.alternateNames.filter((_, i) => i !== index);
    setFormData({ ...formData, alternateNames: newAlternateNames });
  };

  // Calculate stats
  const stats = useMemo(() => {
    if (!Array.isArray(facilities) || facilities.length === 0) {
      return {
        total: 0,
        active: 0,
        inactive: 0,
        pending: 0,
      };
    }

    return {
      total: facilities.length,
      active: facilities.filter((f) => f.status === 'active').length,
      inactive: facilities.filter((f) => f.status === 'inactive').length,
      pending: facilities.filter((f) => f.status === 'pending').length,
    };
  }, [facilities]);

  const handleOpenAddDialog = () => {
    resetForm();
    setFormData(prev => ({
      ...prev,
      facilityId: generateFacilityId(),
    }));
    setAddDialogOpen(true);
  };

  const states = [
    'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut',
    'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa',
    'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan',
    'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire',
    'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio',
    'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota',
    'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia',
    'Wisconsin', 'Wyoming'
  ];

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            Facility Management
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Manage and monitor all registered healthcare facilities
          </Typography>
        </Box>
        <GradientButton
          variant="contained"
          startIcon={<Add />}
          onClick={handleOpenAddDialog}
          sx={{
            borderRadius: '12px',
            textTransform: 'none',
            px: 3,
            py: 1,
          }}
        >
          Add New Facility
        </GradientButton>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert 
          severity="error" 
          sx={{ mb: 3, borderRadius: '12px' }} 
          onClose={() => setError('')}
          icon={false}
        >
          <Box display="flex" alignItems="center">
            <Cancel sx={{ mr: 1 }} />
            <Typography variant="body2">{error}</Typography>
          </Box>
        </Alert>
      )}
      {success && (
        <Alert 
          severity="success" 
          sx={{ mb: 3, borderRadius: '12px' }} 
          onClose={() => setSuccess('')}
          icon={false}
        >
          <Box display="flex" alignItems="center">
            <CheckCircle sx={{ mr: 1 }} />
            <Typography variant="body2">{success}</Typography>
          </Box>
        </Alert>
      )}

      {/* Stats */}
      <Grid container spacing={1.5} mb={2}>
        <Grid item xs={12} sm={6} md={3}>
          <StatsBox
            icon={<Business sx={{ color: 'white', fontSize: 20 }} />}
            label="Total Facilities"
            value={stats.total}
            color={teslaColors.primary}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsBox
            icon={<CheckCircle sx={{ color: 'white', fontSize: 20 }} />}
            label="Active"
            value={stats.active}
            color={teslaColors.success}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsBox
            icon={<Cancel sx={{ color: 'white', fontSize: 20 }} />}
            label="Inactive"
            value={stats.inactive}
            color={teslaColors.error}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsBox
            icon={<Business sx={{ color: 'white', fontSize: 20 }} />}
            label="Pending"
            value={stats.pending}
            color={teslaColors.warning}
          />
        </Grid>
      </Grid>

      {/* Filters and Actions */}
      <GlassCard sx={{ mb: 1.5, borderRadius: '10px' }}>
        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Grid container spacing={1.5} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search by facility name, ID, email, or alternate names..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ fontSize: 18 }} />
                    </InputAdornment>
                  ),
                  sx: { borderRadius: '8px', fontSize: '0.8rem', height: '36px' },
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ fontSize: '0.8rem' }}>Filter by Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Filter by Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                  sx={{ borderRadius: '8px', fontSize: '0.8rem', height: '36px' }}
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="suspended">Suspended</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Button
                fullWidth
                size="small"
                variant="outlined"
                startIcon={<Refresh sx={{ fontSize: 16 }} />}
                onClick={fetchFacilities}
                disabled={loading}
                sx={{ 
                  borderRadius: '8px', 
                  height: '36px',
                  fontSize: '0.8rem',
                  textTransform: 'none',
                  fontWeight: 600,
                  borderColor: alpha(teslaColors.primary, 0.3),
                  '&:hover': {
                    borderColor: teslaColors.primary,
                    background: alpha(teslaColors.primary, 0.05),
                  }
                }}
              >
                Refresh
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </GlassCard>

      {/* Facilities Table */}
      <GlassCard sx={{ borderRadius: '10px' }}>
        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Typography variant="h6" fontWeight={600} sx={{ mb: 1.5, fontSize: '0.95rem' }}>
            Registered Facilities ({filteredFacilities.length})
          </Typography>

          {loading ? (
            <Box textAlign="center" py={8}>
              <LinearProgress sx={{ mb: 2, borderRadius: 6 }} />
              <Typography variant="body2" color="textSecondary">
                Loading facilities...
              </Typography>
            </Box>
          ) : filteredFacilities.length === 0 ? (
            <Box textAlign="center" py={8}>
              <Business sx={{ fontSize: 64, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
              <Typography variant="h6" color="textSecondary" gutterBottom>
                No Facilities Found
              </Typography>
              <Typography variant="body2" color="textSecondary" mb={3}>
                {searchTerm || statusFilter !== 'all'
                  ? 'Try adjusting your search or filters'
                  : 'Get started by adding your first facility'}
              </Typography>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={handleOpenAddDialog}
                sx={{ borderRadius: '12px' }}
              >
                Add First Facility
              </Button>
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 36, py: 0.75, px: 0.75 }} />
                    <TableCell sx={{ py: 0.75, px: 1, fontSize: '0.78rem', fontWeight: 700 }}>Facility ID</TableCell>
                    <TableCell sx={{ py: 0.75, px: 1, fontSize: '0.78rem', fontWeight: 700 }}>Facility Name</TableCell>
                    <TableCell sx={{ py: 0.75, px: 1, fontSize: '0.78rem', fontWeight: 700 }}>Location</TableCell>
                    <TableCell sx={{ py: 0.75, px: 1, fontSize: '0.78rem', fontWeight: 700 }}>Radiologist(s)</TableCell>
                    <TableCell sx={{ py: 0.75, px: 1, fontSize: '0.78rem', fontWeight: 700 }}>Contact Person</TableCell>
                    <TableCell sx={{ py: 0.75, px: 1, fontSize: '0.78rem', fontWeight: 700 }}>Status</TableCell>
                    <TableCell sx={{ py: 0.75, px: 1, fontSize: '0.78rem', fontWeight: 700 }}>Users</TableCell>
                    <TableCell sx={{ py: 0.75, px: 1, fontSize: '0.78rem', fontWeight: 700 }}>Studies</TableCell>
                    <TableCell sx={{ py: 0.75, px: 1, fontSize: '0.78rem', fontWeight: 700 }}>Last Activity</TableCell>
                    <TableCell align="right" sx={{ py: 0.75, px: 1, fontSize: '0.78rem', fontWeight: 700 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredFacilities.map((facility) => (
                    <React.Fragment key={facility._id}>
                      <TableRow 
                        hover
                        sx={{ 
                          '&:hover': { backgroundColor: alpha(teslaColors.primary, 0.02) },
                          cursor: 'pointer',
                          backgroundColor: expandedFacility === facility._id ? alpha(teslaColors.primary, 0.05) : 'inherit'
                        }}
                        onClick={() => handleViewFacilityImages(facility)}
                      >
                        <TableCell sx={{ py: 0.6, px: 0.75 }}>
                          <IconButton size="small">
                            {expandedFacility === facility._id ? <KeyboardArrowUp sx={{ fontSize: 18 }} /> : <KeyboardArrowDown sx={{ fontSize: 18 }} />}
                          </IconButton>
                        </TableCell>
                        <TableCell sx={{ py: 0.6, px: 1 }}>
                          <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: 'primary.main' }}>
                            {facility.facilityId || facility.facilityCode || 'N/A'}
                          </Typography>
                        </TableCell>

                        <TableCell sx={{ py: 0.6, px: 1 }}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Avatar
                              sx={{
                                width: 30,
                                height: 30,
                                background: `linear-gradient(45deg, ${teslaColors.primary}, ${teslaColors.secondary})`,
                                fontWeight: 700,
                                fontSize: '0.8rem',
                              }}
                            >
                              {facility.facilityName?.charAt(0) || 'F'}
                            </Avatar>
                            <Box>
                              <Typography sx={{ fontSize: '0.82rem', fontWeight: 600 }}>
                                {facility.facilityName || 'Unnamed Facility'}
                              </Typography>
                              {Array.isArray(facility.alternateNames) && facility.alternateNames.length > 0 && (
                                <Typography variant="caption" color="textSecondary" display="block" sx={{ fontSize: '0.7rem' }}>
                                  Alt: {facility.alternateNames.join(', ')}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </TableCell>

                        <TableCell>
                          <Box>
                            <Typography variant="body2">
                              {facility.city && facility.state
                                ? `${facility.city}, ${facility.state}`
                                : facility.city || facility.state || 'N/A'}
                            </Typography>
                            <Typography variant="caption" color="textSecondary" display="block">
                              {facility.country}
                            </Typography>
                          </Box>
                        </TableCell>

                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <Box display="flex" flexDirection="column" gap={0.75}>

                            {/* --- Facility-Based Radiologist(s) (hospital's own staff) --- */}
                            <Box>
                              <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 600, textTransform: 'uppercase', fontSize: '0.62rem', letterSpacing: 0.5 }}>
                                Facility
                              </Typography>
                              <Box mt={0.25}>
                                {Array.isArray(facility.facilityRadiologists) && facility.facilityRadiologists.length > 0 ? (
                                  facility.facilityRadiologists.map((rad) => (
                                    <Chip
                                      key={rad._id}
                                      label={`Dr. ${rad.firstName} ${rad.lastName}`}
                                      size="small"
                                      variant="outlined"
                                      sx={{
                                        fontWeight: 600,
                                        borderRadius: '8px',
                                        mr: 0.5,
                                        mb: 0.25,
                                        color: 'text.secondary',
                                        borderColor: 'divider',
                                        fontSize: '0.72rem',
                                      }}
                                    />
                                  ))
                                ) : (
                                  <Typography variant="caption" color="text.disabled" sx={{ fontStyle: 'italic' }}>None</Typography>
                                )}
                              </Box>
                            </Box>

                            {/* --- DycoVue In-House Radiologist (network/teleradiology) --- */}
                            <Box>
                              <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 600, textTransform: 'uppercase', fontSize: '0.62rem', letterSpacing: 0.5 }}>
                                In-House (DycoVue)
                              </Typography>
                              <Box mt={0.25}>
                                {facility.inHouseRadiologist ? (
                                  <Tooltip title={`Assigned DycoVue Radiologist: ${facility.inHouseRadiologist.email || ''} — click to reassign`}>
                                    <Chip
                                      icon={<LocalHospital sx={{ fontSize: '15px !important', color: `${teslaColors.primary} !important` }} />}
                                      label={`Dr. ${facility.inHouseRadiologist.firstName} ${facility.inHouseRadiologist.lastName}`}
                                      size="small"
                                      variant="outlined"
                                      onClick={() => handleOpenAssignRadiologistDialog(facility)}
                                      sx={{
                                        fontWeight: 600,
                                        cursor: 'pointer',
                                        borderRadius: '8px',
                                        color: teslaColors.primary,
                                        borderColor: alpha(teslaColors.primary, 0.4),
                                        background: alpha(teslaColors.primary, 0.05),
                                        '&:hover': {
                                          background: alpha(teslaColors.primary, 0.12),
                                          borderColor: teslaColors.primary,
                                        }
                                      }}
                                    />
                                  </Tooltip>
                                ) : (
                                  <Chip
                                    label="+ Assign"
                                    size="small"
                                    variant="outlined"
                                    onClick={() => handleOpenAssignRadiologistDialog(facility)}
                                    sx={{
                                      color: 'text.secondary',
                                      borderColor: 'divider',
                                      cursor: 'pointer',
                                      borderRadius: '8px',
                                      fontSize: '0.72rem',
                                      borderStyle: 'dashed',
                                      '&:hover': {
                                        borderColor: teslaColors.primary,
                                        color: teslaColors.primary,
                                        background: alpha(teslaColors.primary, 0.05),
                                      }
                                    }}
                                  />
                                )}
                              </Box>
                            </Box>

                          </Box>
                        </TableCell>

                        <TableCell>
                          <Box>
                            <Typography variant="body2">
                              {facility.contactPerson?.firstName} {facility.contactPerson?.lastName}
                            </Typography>
                            <Typography variant="caption" color="textSecondary" display="block">
                              {facility.contactPerson?.email || facility.email}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {facility.contactPerson?.phoneNumber || facility.phoneNumber}
                            </Typography>
                          </Box>
                        </TableCell>

                        <TableCell>
                          <StatusChip status={facility.status} />
                        </TableCell>

                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>
                            {facility.totalUsers || facility.userCount || 0}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>
                            {facility.studyCount || 0}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          <Typography variant="body2">
                            {facility.lastActivity
                              ? new Date(facility.lastActivity).toLocaleDateString()
                              : 'N/A'}
                          </Typography>
                        </TableCell>

                        <TableCell align="right">
                          <Box display="flex" gap={0.5} justifyContent="flex-end">
                            <Tooltip title="View Reports">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleViewReports(facility);
                                }}
                                sx={{
                                  '&:hover': {
                                    background: alpha(teslaColors.info, 0.1),
                                  }
                                }}
                              >
                                <Description fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="More actions">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleMenuOpen(e, facility);
                                }}
                                sx={{
                                  '&:hover': {
                                    background: alpha(teslaColors.primary, 0.1),
                                  }
                                }}
                              >
                                <MoreVert fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                      
                      {/* Expanded Row for Facility Studies */}
                      <TableRow>
                        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={11}>
                          <Collapse in={expandedFacility === facility._id} timeout="auto" unmountOnExit>
                            <Box sx={{ margin: 2 }}>
                              <Typography variant="h6" gutterBottom component="div" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <ImageIcon color="primary" />
                                Studies for {facility.facilityName}
                              </Typography>
                              
                              {studiesLoading ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                                  <CircularProgress size={24} />
                                </Box>
                              ) : facilityStudies.length === 0 ? (
                                <Alert severity="info" sx={{ borderRadius: '12px' }}>
                                  No studies found for this facility.
                                </Alert>
                              ) : (
                                <TableContainer component={Paper} sx={{ borderRadius: '12px', overflow: 'hidden', border: `1px solid ${alpha(teslaColors.primary, 0.1)}` }}>
                                  <Table size="small">
                                    <TableHead sx={{ backgroundColor: alpha(teslaColors.primary, 0.05) }}>
                                      <TableRow>
                                        <TableCell><strong>Patient Name</strong></TableCell>
                                        <TableCell><strong>Patient ID</strong></TableCell>
                                        <TableCell><strong>Modality</strong></TableCell>
                                        <TableCell><strong>Study Date</strong></TableCell>
                                        <TableCell><strong>Description</strong></TableCell>
                                        <TableCell><strong>Reports</strong></TableCell>
                                        <TableCell align="center"><strong>Action</strong></TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {facilityStudies.map((study) => (
                                        <TableRow key={study._id} hover>
                                          <TableCell>{study.patientName || 'N/A'}</TableCell>
                                          <TableCell>{study.patientId || 'N/A'}</TableCell>
                                          <TableCell>
                                            <Chip label={study.modality || 'N/A'} size="small" variant="outlined" />
                                          </TableCell>
                                          <TableCell>{study.studyDate ? new Date(study.studyDate).toLocaleDateString() : 'N/A'}</TableCell>
                                          <TableCell>
                                            <Typography variant="caption" noWrap sx={{ maxWidth: 150, display: 'block' }}>
                                              {study.studyDescription || 'N/A'}
                                            </Typography>
                                          </TableCell>
                                          <TableCell>
                                            <Box display="flex" gap={0.5}>
                                              {study.selfReportStatus && (
                                                <ReportStatusBadge status={study.selfReportStatus} type="self-report" />
                                              )}
                                              {study.dycovueReportStatus && (
                                                <ReportStatusBadge status={study.dycovueReportStatus} type="dycovue-report" />
                                              )}
                                            </Box>
                                          </TableCell>
                                          <TableCell align="center">
                                            <Tooltip title="View in OHIF Viewer">
                                              <IconButton 
                                                size="small" 
                                                color="primary"
                                                onClick={() => {
                                                  const ohifUrl = process.env.REACT_APP_OHIF_URL || 'https://ohif-viewer.dycovue.com';
                                                  const uid = study.studyInstanceUID || study.orthancStudyId;
                                                  if (uid) {
                                                    window.open(`${ohifUrl}/viewer/${uid}`, '_blank');
                                                  } else {
                                                    alert('Study UID missing');
                                                  }
                                                }}
                                              >
                                                <Visibility fontSize="small" />
                                              </IconButton>
                                            </Tooltip>
                                          </TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                  <TablePagination
                                    component="div"
                                    count={totalStudies}
                                    page={studiesPage}
                                    onPageChange={handleStudiesPageChange}
                                    rowsPerPage={studiesRowsPerPage}
                                    onRowsPerPageChange={handleStudiesRowsPerPageChange}
                                    rowsPerPageOptions={[5, 10, 25]}
                                    size="small"
                                  />
                                </TableContainer>
                              )}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </GlassCard>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        PaperProps={{
          sx: {
            borderRadius: '12px',
            minWidth: 180,
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            mt: 1,
          }
        }}
      >
        <MuiMenuItem onClick={() => handleViewClick(selectedFacility)}>
          <Visibility fontSize="small" sx={{ mr: 1.5 }} />
          View Details
        </MuiMenuItem>
        <MuiMenuItem onClick={() => handleViewFacilityImages(selectedFacility)}>
          <DashboardIcon fontSize="small" sx={{ mr: 1.5 }} />
          {expandedFacility === selectedFacility?._id ? 'Hide Facility Images' : 'View Facility Images'}
        </MuiMenuItem>
        <MuiMenuItem onClick={() => handleEditClick(selectedFacility)}>
          <Edit fontSize="small" sx={{ mr: 1.5 }} />
          Edit Facility
        </MuiMenuItem>
        <MuiMenuItem onClick={() => handleOpenAssignRadiologistDialog(selectedFacility)}>
          <LocalHospital fontSize="small" sx={{ mr: 1.5, color: teslaColors.primary }} />
          Assign In-House Radiologist
        </MuiMenuItem>
        <MuiMenuItem onClick={() => handleIntegrationClick(selectedFacility)}>
          <SettingsEthernet fontSize="small" sx={{ mr: 1.5 }} />
          Connect Scanners & EMR
        </MuiMenuItem>
        <Divider sx={{ my: 0.5 }} />
        <MuiMenuItem onClick={handleStatusMenuOpen}>
          <CheckCircle fontSize="small" sx={{ mr: 1.5 }} />
          Change Status
          <ArrowDropDown sx={{ ml: 'auto' }} />
        </MuiMenuItem>
        <Divider sx={{ my: 0.5 }} />
        <MuiMenuItem 
          onClick={() => handleDeleteClick(selectedFacility)} 
          sx={{ 
            color: 'error.main',
            '&:hover': {
              backgroundColor: alpha(teslaColors.error, 0.1),
            }
          }}
        >
          <Delete fontSize="small" sx={{ mr: 1.5 }} />
          Delete Facility
        </MuiMenuItem>
      </Menu>

      {/* Status Change Submenu */}
      <Menu
        anchorEl={statusMenuAnchorEl}
        open={Boolean(statusMenuAnchorEl)}
        onClose={handleStatusMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'left', vertical: 'top' }}
        PaperProps={{
          sx: {
            borderRadius: '12px',
            minWidth: 150,
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            mt: 0.5,
          }
        }}
      >
        <MuiMenuItem 
          onClick={() => handleQuickStatusChange('active')}
          disabled={selectedFacility?.status === 'active'}
          sx={{
            color: selectedFacility?.status === 'active' ? 'text.secondary' : teslaColors.success,
            '&:hover': {
              backgroundColor: alpha(teslaColors.success, 0.1),
            }
          }}
        >
          <CheckCircle fontSize="small" sx={{ mr: 1.5 }} />
          Active
        </MuiMenuItem>
        <MuiMenuItem 
          onClick={() => handleQuickStatusChange('pending')}
          disabled={selectedFacility?.status === 'pending'}
          sx={{
            color: selectedFacility?.status === 'pending' ? 'text.secondary' : teslaColors.warning,
            '&:hover': {
              backgroundColor: alpha(teslaColors.warning, 0.1),
            }
          }}
        >
          <Cancel fontSize="small" sx={{ mr: 1.5 }} />
          Pending
        </MuiMenuItem>
        <MuiMenuItem 
          onClick={() => handleQuickStatusChange('inactive')}
          disabled={selectedFacility?.status === 'inactive'}
          sx={{
            color: selectedFacility?.status === 'inactive' ? 'text.secondary' : teslaColors.error,
            '&:hover': {
              backgroundColor: alpha(teslaColors.error, 0.1),
            }
          }}
        >
          <Cancel fontSize="small" sx={{ mr: 1.5 }} />
          Inactive
        </MuiMenuItem>
        <MuiMenuItem 
          onClick={() => handleQuickStatusChange('suspended')}
          disabled={selectedFacility?.status === 'suspended'}
          sx={{
            color: selectedFacility?.status === 'suspended' ? 'text.secondary' : teslaColors.error,
            '&:hover': {
              backgroundColor: alpha(teslaColors.error, 0.1),
            }
          }}
        >
          <Cancel fontSize="small" sx={{ mr: 1.5 }} />
          Suspended
        </MuiMenuItem>
      </Menu>

      {/* Add Facility Dialog */}
      <Dialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        maxWidth={false}
        PaperProps={{
          sx: {
            width: { xs: '95vw', sm: '92vw', md: '860px' },
            maxWidth: '880px !important',
            maxHeight: '88vh',
            borderRadius: '16px',
            bgcolor: cardBg,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          },
        }}
      >
        <DialogTitle sx={{ px: 2.5, py: 1.25, borderBottom: `1px solid ${borderColor}`, bgcolor: cardBg }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="subtitle1" fontWeight={700} sx={{ color: textColorPrimary, fontSize: '1rem' }} component="div">
                Add New Facility
              </Typography>
              <Typography variant="body2" sx={{ color: textColorSecondary, fontSize: '0.78rem' }} component="div">
                Enter the details for the new facility
              </Typography>
            </Box>
            <IconButton onClick={() => setAddDialogOpen(false)} size="small" sx={{ color: textColorSecondary }}>
              <Close sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 2.5, overflowY: 'auto' }}>
          <Grid container spacing={3}>
            {/* Facility Information */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom fontWeight={600} color="primary">
                Facility Information
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Facility Name"
                value={formData.facilityName}
                onChange={(e) => handleFormChange('facilityName', e.target.value)}
                placeholder="Enter facility name"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                disabled
                label="Facility ID"
                value={formData.facilityId}
                helperText="Auto-generated"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Facility Code"
                value={formData.facilityCode}
                onChange={(e) => handleFormChange('facilityCode', e.target.value)}
                placeholder="Enter facility code"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.status}
                  label="Status"
                  onChange={(e) => handleFormChange('status', e.target.value)}
                  sx={{ borderRadius: '12px' }}
                >
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Alternate Names */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Alternate Facility Names (Optional)
              </Typography>
              <Typography variant="caption" color="textSecondary" display="block" mb={2}>
                Add alternate names if the facility saves DICOM images under different names
              </Typography>
              <Stack spacing={2}>
                {formData.alternateNames.map((name, index) => (
                  <Box key={index} display="flex" gap={1}>
                    <TextField
                      fullWidth
                      label={`Alternate Name ${index + 1}`}
                      value={name}
                      onChange={(e) => handleAlternateNameChange(index, e.target.value)}
                      placeholder="Enter alternate name"
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                    />
                    {formData.alternateNames.length > 1 && (
                      <IconButton 
                        color="error" 
                        onClick={() => removeAlternateName(index)}
                        sx={{ borderRadius: '8px' }}
                      >
                        <Delete />
                      </IconButton>
                    )}
                  </Box>
                ))}
                <Button
                  variant="outlined"
                  startIcon={<Add />}
                  onClick={addAlternateName}
                  sx={{ borderRadius: '12px', alignSelf: 'flex-start' }}
                >
                  Add Alternate Name
                </Button>
              </Stack>
            </Grid>

            {/* Address */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Address"
                value={formData.address}
                onChange={(e) => handleFormChange('address', e.target.value)}
                placeholder="Street address"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LocationOn />
                    </InputAdornment>
                  ),
                }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                required
                label="City"
                value={formData.city}
                onChange={(e) => handleFormChange('city', e.target.value)}
                placeholder="City"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <Autocomplete
                freeSolo
                options={states}
                value={formData.state}
                onChange={(e, newValue) => handleFormChange('state', newValue || '')}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="State"
                    placeholder="State"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="ZIP Code"
                value={formData.zipCode}
                onChange={(e) => handleFormChange('zipCode', e.target.value)}
                placeholder="ZIP Code"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Country"
                value={formData.country}
                onChange={(e) => handleFormChange('country', e.target.value)}
                placeholder="Country"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
              />
            </Grid>

            {/* Contact Information */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" gutterBottom fontWeight={600} color="primary">
                Facility Contact Information
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone Number"
                value={formData.phoneNumber}
                onChange={(e) => handleFormChange('phoneNumber', e.target.value)}
                placeholder="+1 (555) 000-0000"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Phone />
                    </InputAdornment>
                  ),
                }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                type="email"
                label="Email Address"
                value={formData.email}
                onChange={(e) => handleFormChange('email', e.target.value)}
                placeholder="contact@facility.com"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Email />
                    </InputAdornment>
                  ),
                }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
              />
            </Grid>

            {/* Contact Person */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" gutterBottom fontWeight={600} color="primary">
                Contact Person Information
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="First Name"
                value={formData.contactPerson.firstName}
                onChange={(e) => handleContactPersonChange('firstName', e.target.value)}
                placeholder="Enter first name"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Person />
                    </InputAdornment>
                  ),
                }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Last Name"
                value={formData.contactPerson.lastName}
                onChange={(e) => handleContactPersonChange('lastName', e.target.value)}
                placeholder="Enter last name"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="email"
                label="Email Address"
                value={formData.contactPerson.email}
                onChange={(e) => handleContactPersonChange('email', e.target.value)}
                placeholder="person@facility.com"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Email />
                    </InputAdornment>
                  ),
                }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone Number"
                value={formData.contactPerson.phoneNumber}
                onChange={(e) => handleContactPersonChange('phoneNumber', e.target.value)}
                placeholder="+1 (555) 000-0000"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Phone />
                    </InputAdornment>
                  ),
                }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
              />
            </Grid>

            {/* In-House Radiologist Assignment */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" gutterBottom fontWeight={600} color="primary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LocalHospital fontSize="small" />
                In-House Radiologist Assignment (DycoVue Reader)
              </Typography>
              <Typography variant="caption" color="textSecondary" display="block" mb={2}>
                Assign a dedicated DycoVue in-house radiologist to automatically read and report on all scans from this hospital.
              </Typography>
            </Grid>

            <Grid item xs={12} sm={8}>
              <FormControl fullWidth>
                <InputLabel>Designated In-House Radiologist</InputLabel>
                <Select
                  value={formData.inHouseRadiologist || ''}
                  label="Designated In-House Radiologist"
                  onChange={(e) => handleFormChange('inHouseRadiologist', e.target.value)}
                  sx={{ borderRadius: '12px' }}
                >
                  <MenuItem value="">
                    <em>None (Hospital uses on-site radiologist)</em>
                  </MenuItem>
                  {inHouseRadiologists.map((rad) => (
                    <MenuItem key={rad._id} value={rad._id}>
                      Dr. {rad.firstName} {rad.lastName} ({rad.specialty || 'Radiology'}) — {rad.email}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.autoAssignToInHouse !== false}
                    onChange={(e) => handleFormChange('autoAssignToInHouse', e.target.checked)}
                    color="primary"
                  />
                }
                label={
                  <Typography variant="body2" fontWeight={500}>
                    Auto-Route Scans
                  </Typography>
                }
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1.25, borderTop: `1px solid ${borderColor}`, bgcolor: cardBg }}>
          <Button 
            onClick={() => setAddDialogOpen(false)} 
            sx={{ borderRadius: '8px' }}
          >
            Cancel
          </Button>
          <GradientButton
            onClick={handleAddFacility}
            disabled={actionLoading || !formData.facilityName || !formData.email}
            sx={{ borderRadius: '8px', px: 2.5 }}
          >
            {actionLoading ? 'Creating...' : 'Create Facility'}
          </GradientButton>
        </DialogActions>
      </Dialog>

      {/* Edit Facility Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth={false}
        PaperProps={{
          sx: {
            width: { xs: '95vw', sm: '92vw', md: '860px' },
            maxWidth: '880px !important',
            maxHeight: '88vh',
            borderRadius: '16px',
            bgcolor: cardBg,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          },
        }}
      >
        <DialogTitle sx={{ px: 2.5, py: 1.25, borderBottom: `1px solid ${borderColor}`, bgcolor: cardBg }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="subtitle1" fontWeight={700} sx={{ color: textColorPrimary, fontSize: '1rem' }} component="div">
                Edit Facility
              </Typography>
              <Typography variant="body2" sx={{ color: textColorSecondary, fontSize: '0.78rem' }} component="div">
                Update facility information
              </Typography>
            </Box>
            <IconButton onClick={() => setEditDialogOpen(false)} size="small" sx={{ color: textColorSecondary }}>
              <Close sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 2.5, overflowY: 'auto' }}>
          <Grid container spacing={3}>
            {/* Same form structure as Add Dialog */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom fontWeight={600} color="primary">
                Facility Information
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Facility Name"
                value={formData.facilityName}
                onChange={(e) => handleFormChange('facilityName', e.target.value)}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                disabled
                label="Facility ID"
                value={formData.facilityId}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
              />
            </Grid>

            {/* Logo Upload Section */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" gutterBottom fontWeight={600} color="primary">
                Facility Logo
              </Typography>
              <Typography variant="caption" color="textSecondary" display="block" mb={2}>
                Upload a logo that will appear on all reports from this facility. Recommended size: 200x200px. Max size: 2MB.
              </Typography>
              
              <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
                {logoPreview && (
                  <Box
                    sx={{
                      width: 120,
                      height: 120,
                      border: `2px solid ${alpha(teslaColors.primary, 0.3)}`,
                      borderRadius: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      overflow: 'hidden',
                      background: '#f5f5f5',
                    }}
                  >
                    <img
                      src={logoPreview}
                      alt="Facility logo preview"
                      style={{
                        maxWidth: '100%',
                        maxHeight: '100%',
                        objectFit: 'contain',
                      }}
                    />
                  </Box>
                )}
                
                <Box flex={1} minWidth={200}>
                  <input
                    accept="image/jpeg,image/png,image/jpg,image/webp,image/svg+xml"
                    style={{ display: 'none' }}
                    id="logo-upload"
                    type="file"
                    onChange={handleLogoSelect}
                  />
                  <label htmlFor="logo-upload">
                    <Button
                      variant="outlined"
                      component="span"
                      startIcon={<CloudUpload />}
                      disabled={logoUploading}
                      sx={{
                        borderRadius: '12px',
                        borderColor: alpha(teslaColors.primary, 0.3),
                        '&:hover': {
                          borderColor: teslaColors.primary,
                          background: alpha(teslaColors.primary, 0.05),
                        },
                      }}
                    >
                      {logoPreview ? 'Change Logo' : 'Upload Logo'}
                    </Button>
                  </label>
                  
                  {logoPreview && (
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<Delete />}
                      onClick={handleLogoDelete}
                      disabled={logoUploading}
                      sx={{
                        ml: 1,
                        borderRadius: '12px',
                      }}
                    >
                      Remove
                    </Button>
                  )}
                </Box>
              </Box>
            </Grid>

            {/* ... Rest of the edit form (same as add form but with current values) */}
            {/* For brevity, including the same form structure as Add Dialog */}

            {/* Alternate Names (Edit) */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Alternate Facility Names (Optional)
              </Typography>
              <Typography variant="caption" color="textSecondary" display="block" mb={2}>
                Add alternate names if the facility saves DICOM images under different names
              </Typography>
              <Stack spacing={2}>
                {formData.alternateNames.map((name, index) => (
                  <Box key={index} display="flex" gap={1}>
                    <TextField
                      fullWidth
                      label={`Alternate Name ${index + 1}`}
                      value={name}
                      onChange={(e) => handleAlternateNameChange(index, e.target.value)}
                      placeholder="Enter alternate name"
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                    />
                    {formData.alternateNames.length > 1 && (
                      <IconButton 
                        color="error" 
                        onClick={() => removeAlternateName(index)}
                        sx={{ borderRadius: '8px' }}
                      >
                        <Delete />
                      </IconButton>
                    )}
                  </Box>
                ))}
                <Button
                  variant="outlined"
                  startIcon={<Add />}
                  onClick={addAlternateName}
                  sx={{ borderRadius: '12px', alignSelf: 'flex-start' }}
                >
                  Add Alternate Name
                </Button>
              </Stack>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Address"
                value={formData.address}
                onChange={(e) => handleFormChange('address', e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LocationOn />
                    </InputAdornment>
                  ),
                }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                type="email"
                label="Email Address"
                value={formData.email}
                onChange={(e) => handleFormChange('email', e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Email />
                    </InputAdornment>
                  ),
                }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.status}
                  label="Status"
                  onChange={(e) => handleFormChange('status', e.target.value)}
                  sx={{ borderRadius: '12px' }}
                >
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* In-House Radiologist Assignment (Edit) */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" gutterBottom fontWeight={600} color="primary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LocalHospital fontSize="small" />
                In-House Radiologist Assignment (DycoVue Reader)
              </Typography>
              <Typography variant="caption" color="textSecondary" display="block" mb={2}>
                Assign a dedicated DycoVue in-house radiologist to automatically read and report on all scans from this hospital.
              </Typography>
            </Grid>

            <Grid item xs={12} sm={8}>
              <FormControl fullWidth>
                <InputLabel>Designated In-House Radiologist</InputLabel>
                <Select
                  value={formData.inHouseRadiologist || ''}
                  label="Designated In-House Radiologist"
                  onChange={(e) => handleFormChange('inHouseRadiologist', e.target.value)}
                  sx={{ borderRadius: '12px' }}
                >
                  <MenuItem value="">
                    <em>None (Hospital uses on-site radiologist)</em>
                  </MenuItem>
                  {inHouseRadiologists.map((rad) => (
                    <MenuItem key={rad._id} value={rad._id}>
                      Dr. {rad.firstName} {rad.lastName} ({rad.specialty || 'Radiology'}) — {rad.email}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.autoAssignToInHouse !== false}
                    onChange={(e) => handleFormChange('autoAssignToInHouse', e.target.checked)}
                    color="primary"
                  />
                }
                label={
                  <Typography variant="body2" fontWeight={500}>
                    Auto-Route Scans
                  </Typography>
                }
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1.25, borderTop: `1px solid ${borderColor}`, bgcolor: cardBg }}>
          <Button 
            onClick={() => setEditDialogOpen(false)} 
            sx={{ borderRadius: '8px' }}
          >
            Cancel
          </Button>
          <GradientButton
            onClick={handleUpdateFacility}
            disabled={actionLoading || logoUploading || !formData.facilityName || !formData.email}
            sx={{ borderRadius: '8px', px: 2.5 }}
          >
            {actionLoading || logoUploading ? 'Updating...' : 'Update Facility'}
          </GradientButton>
        </DialogActions>
      </Dialog>

      {/* View Details Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth={false}
        PaperProps={{
          sx: {
            width: { xs: '95vw', sm: '90vw', md: '780px' },
            maxWidth: '800px !important',
            maxHeight: '88vh',
            borderRadius: '16px',
            bgcolor: cardBg,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          },
        }}
      >
        <DialogTitle sx={{ px: 2.5, py: 1.25, borderBottom: `1px solid ${borderColor}`, bgcolor: cardBg }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="subtitle1" fontWeight={700} sx={{ color: textColorPrimary, fontSize: '1rem' }} component="div">
                Facility Details
              </Typography>
              <Typography variant="body2" sx={{ color: textColorSecondary, fontSize: '0.78rem' }} component="div">
                Complete information about the facility
              </Typography>
            </Box>
            <IconButton onClick={() => setViewDialogOpen(false)} size="small" sx={{ color: textColorSecondary }}>
              <Close sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 2.5, overflowY: 'auto' }}>
          {selectedFacility && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Box display="flex" alignItems="center" gap={2} mb={3}>
                  <Avatar
                    sx={{
                      width: 60,
                      height: 60,
                      background: `linear-gradient(45deg, ${teslaColors.primary}, ${teslaColors.secondary})`,
                      fontSize: 24,
                      fontWeight: 600,
                    }}
                  >
                    {selectedFacility.facilityName?.charAt(0) || 'F'}
                  </Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight={600}>
                      {selectedFacility.facilityName}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {selectedFacility.facilityId || selectedFacility.facilityCode}
                    </Typography>
                  </Box>
                  <Box flexGrow={1} />
                  <StatusChip status={selectedFacility.status} />
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  Facility Name
                </Typography>
                <Typography variant="body1" fontWeight={500}>
                  {selectedFacility.facilityName}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  Facility ID
                </Typography>
                <Typography variant="body1" fontWeight={500}>
                  {selectedFacility.facilityId || selectedFacility.facilityCode}
                </Typography>
              </Grid>

              {Array.isArray(selectedFacility.alternateNames) && selectedFacility.alternateNames.length > 0 && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    Alternate Names
                  </Typography>
                  <Box display="flex" flexWrap="wrap" gap={1}>
                    {selectedFacility.alternateNames.map((name, index) => (
                      <Chip
                        key={index}
                        label={name}
                        size="small"
                        sx={{
                          background: alpha(teslaColors.primary, 0.1),
                          color: teslaColors.primary,
                        }}
                      />
                    ))}
                  </Box>
                </Grid>
              )}

              <Grid item xs={12}>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  Address
                </Typography>
                <Typography variant="body1">
                  {selectedFacility.address}
                  {selectedFacility.city && selectedFacility.state && (
                    <>
                      <br />
                      {selectedFacility.city}, {selectedFacility.state} {selectedFacility.zipCode}
                    </>
                  )}
                  {selectedFacility.country && (
                    <>
                      <br />
                      {selectedFacility.country}
                    </>
                  )}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  Contact Person
                </Typography>
                <Typography variant="body1">
                  {selectedFacility.contactPerson?.firstName} {selectedFacility.contactPerson?.lastName}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  Contact Email
                </Typography>
                <Typography variant="body1">
                  {selectedFacility.contactPerson?.email || selectedFacility.email}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  Phone Number
                </Typography>
                <Typography variant="body1">
                  {selectedFacility.contactPerson?.phoneNumber || selectedFacility.phoneNumber || 'N/A'}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  Registration Date
                </Typography>
                <Typography variant="body1">
                  {selectedFacility.createdAt
                    ? new Date(selectedFacility.createdAt).toLocaleDateString()
                    : 'N/A'}
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" gutterBottom fontWeight={600} color="primary">
                  Activity Summary
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={3}>
                    <Box textAlign="center" p={2} sx={{ background: alpha(teslaColors.primary, 0.05), borderRadius: '12px' }}>
                      <Typography variant="h5" fontWeight={700} color="primary">
                        {selectedFacility.userCount || 0}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Users
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Box textAlign="center" p={2} sx={{ background: alpha(teslaColors.success, 0.05), borderRadius: '12px' }}>
                      <Typography variant="h5" fontWeight={700} color={teslaColors.success}>
                        {selectedFacility.studyCount || 0}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Studies
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Box textAlign="center" p={2} sx={{ background: alpha(teslaColors.warning, 0.05), borderRadius: '12px' }}>
                      <Typography variant="h5" fontWeight={700} color={teslaColors.warning}>
                        {selectedFacility.imageCount || 0}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Images
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Box textAlign="center" p={2} sx={{ background: alpha(teslaColors.info, 0.05), borderRadius: '12px' }}>
                      <Typography variant="h5" fontWeight={700} color={teslaColors.info}>
                        {selectedFacility.reportCount || 0}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Reports
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  Last Activity
                </Typography>
                <Typography variant="body1">
                  {selectedFacility.lastActivity
                    ? new Date(selectedFacility.lastActivity).toLocaleString()
                    : 'No recent activity'}
                </Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1.25, borderTop: `1px solid ${borderColor}`, bgcolor: cardBg }}>
          <Button 
            onClick={() => setViewDialogOpen(false)} 
            sx={{ borderRadius: '8px' }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Facility Reports Dialog */}
      <Dialog
        open={reportsDialogOpen}
        onClose={() => setReportsDialogOpen(false)}
        maxWidth={false}
        PaperProps={{
          sx: {
            width: { xs: '95vw', sm: '92vw', md: '920px' },
            maxWidth: '950px !important',
            maxHeight: '88vh',
            borderRadius: '16px',
            bgcolor: cardBg,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          },
        }}
      >
        <DialogTitle sx={{ px: 2.5, py: 1.25, borderBottom: `1px solid ${borderColor}`, bgcolor: cardBg }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="subtitle1" fontWeight={700} sx={{ color: textColorPrimary, fontSize: '1rem' }} component="div">
                Reports for {selectedFacility?.facilityName}
              </Typography>
              <Typography variant="body2" sx={{ color: textColorSecondary, fontSize: '0.78rem' }} component="div">
                View all patient reports under this facility
              </Typography>
            </Box>
            <IconButton onClick={() => setReportsDialogOpen(false)} size="small" sx={{ color: textColorSecondary }}>
              <Close sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 2, overflowY: 'auto' }}>
          {facilityReportsLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
              <CircularProgress />
            </Box>
          ) : facilityReportsStudies.length === 0 ? (
            <Alert severity="info" sx={{ borderRadius: '8px' }}>
              No studies found for this facility.
            </Alert>
          ) : (
            <TableContainer component={Paper} sx={{ borderRadius: '12px', overflow: 'hidden', maxHeight: '52vh', border: `1px solid ${alpha(teslaColors.primary, 0.1)}` }}>
              <Table>
                <TableHead sx={{ backgroundColor: alpha(teslaColors.primary, 0.05) }}>
                  <TableRow>
                    <TableCell><strong>Patient Name</strong></TableCell>
                    <TableCell><strong>Patient ID</strong></TableCell>
                    <TableCell><strong>Modality</strong></TableCell>
                    <TableCell><strong>Study Date</strong></TableCell>
                    <TableCell><strong>Description</strong></TableCell>
                    <TableCell><strong>Reports</strong></TableCell>
                    <TableCell align="center"><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {facilityReportsStudies.map((study) => (
                    <TableRow key={study._id} hover>
                      <TableCell>{study.patientName || 'N/A'}</TableCell>
                      <TableCell>{study.patientId || 'N/A'}</TableCell>
                      <TableCell>
                        <Chip label={study.modality || 'N/A'} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>{study.studyDate ? new Date(study.studyDate).toLocaleDateString() : 'N/A'}</TableCell>
                      <TableCell>
                        <Typography variant="caption" noWrap sx={{ maxWidth: 150, display: 'block' }}>
                          {study.studyDescription || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" gap={0.5}>
                          {study.selfReportStatus && (
                            <ReportStatusBadge status={study.selfReportStatus} type="self-report" />
                          )}
                          {study.dycovueReportStatus && (
                            <ReportStatusBadge status={study.dycovueReportStatus} type="dycovue-report" />
                          )}
                          {!study.selfReportStatus && !study.dycovueReportStatus && (
                            <Typography variant="caption" color="textSecondary">No reports</Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        {(study.selfReportStatus === 'completed' || study.dycovueReportStatus === 'completed') ? (
                          <Tooltip title="View Reports">
                            <IconButton 
                              size="small" 
                              color="primary"
                              onClick={() => handleViewStudyReport(study)}
                            >
                              <Description fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Typography variant="caption" color="textSecondary">No reports available</Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1.25, borderTop: `1px solid ${borderColor}`, bgcolor: cardBg }}>
          <Button 
            onClick={() => setReportsDialogOpen(false)} 
            sx={{ borderRadius: '8px' }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Report Viewer Dialog */}
      <Dialog
        open={reportViewerDialogOpen}
        onClose={() => setReportViewerDialogOpen(false)}
        maxWidth={false}
        PaperProps={{
          sx: {
            width: { xs: '95vw', sm: '92vw', md: '880px', lg: '920px' },
            maxWidth: '940px !important',
            maxHeight: '88vh',
            borderRadius: '16px',
            bgcolor: cardBg,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          },
        }}
      >
        <DialogTitle sx={{ px: 2.5, py: 1.25, borderBottom: `1px solid ${borderColor}`, bgcolor: cardBg }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="subtitle1" fontWeight={700} sx={{ color: textColorPrimary, fontSize: '1rem' }} component="div">
                Report Viewer
              </Typography>
              <Typography variant="body2" sx={{ color: textColorSecondary, fontSize: '0.78rem' }} component="div">
                {selectedStudyForReport?.patientName} - {selectedStudyForReport?.modality}
              </Typography>
            </Box>
            <IconButton onClick={() => setReportViewerDialogOpen(false)} size="small" sx={{ color: textColorSecondary }}>
              <Close sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 2, overflowY: 'auto' }}>
          {selectedStudyForReport && (
            <ReportViewer studyId={selectedStudyForReport._id} study={selectedStudyForReport} />
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1.25, borderTop: `1px solid ${borderColor}`, bgcolor: cardBg }}>
          <Button 
            onClick={() => setReportViewerDialogOpen(false)} 
            sx={{ borderRadius: '8px' }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth={false}
        PaperProps={{
          sx: {
            width: { xs: '95vw', sm: '460px' },
            maxWidth: '480px !important',
            maxHeight: '85vh',
            borderRadius: '16px',
            bgcolor: cardBg,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          },
        }}
      >
        <DialogTitle sx={{ px: 2.5, py: 1.25, borderBottom: `1px solid ${borderColor}`, bgcolor: cardBg }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="subtitle1" fontWeight={700} color="error" component="div">
                Delete Facility
              </Typography>
              <Typography variant="body2" sx={{ color: textColorSecondary, fontSize: '0.78rem' }} component="div">
                This action cannot be undone
              </Typography>
            </Box>
            <IconButton onClick={() => setDeleteDialogOpen(false)} size="small" sx={{ color: textColorSecondary }}>
              <Close sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 2.5, overflowY: 'auto' }}>
          <Box display="flex" alignItems="center" gap={2} mb={3}>
            <Avatar
              sx={{
                width: 48,
                height: 48,
                background: `linear-gradient(45deg, ${teslaColors.error}, ${alpha(teslaColors.error, 0.7)})`,
              }}
            >
              <Delete />
            </Avatar>
            <Box>
              <Typography variant="body1" fontWeight={500}>
                Are you sure you want to delete this facility?
              </Typography>
              <Typography variant="body2" color="textSecondary">
                This action cannot be undone. All associated data will be permanently removed.
              </Typography>
            </Box>
          </Box>

          {selectedFacility && (
            <Box sx={{ p: 2, background: alpha(teslaColors.error, 0.05), borderRadius: '12px' }}>
              <Typography variant="subtitle2" gutterBottom>
                Facility to be deleted:
              </Typography>
              <Typography variant="body1" fontWeight={500}>
                {selectedFacility.facilityName}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                ID: {selectedFacility.facilityId || selectedFacility.facilityCode}
              </Typography>
              {(selectedFacility.userCount > 0 || selectedFacility.studyCount > 0) && (
                <Typography variant="caption" color="error" display="block" mt={1}>
                  This will permanently delete {selectedFacility.userCount || 0} user(s), {selectedFacility.studyCount || 0} study/studies, and all associated reports and images.
                </Typography>
              )}
            </Box>
          )}
          {deleteError && (
            <Alert severity="error" sx={{ mt: 2, borderRadius: '8px' }} onClose={() => setDeleteError('')}>
              {deleteError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1.25, borderTop: `1px solid ${borderColor}`, bgcolor: cardBg }}>
          <Button 
            onClick={() => setDeleteDialogOpen(false)} 
            sx={{ borderRadius: '8px' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteConfirm}
            disabled={actionLoading}
            sx={{ 
              borderRadius: '8px',
              px: 2.5,
              background: `linear-gradient(45deg, ${teslaColors.error}, ${alpha(teslaColors.error, 0.8)})`,
            }}
          >
            {actionLoading ? 'Deleting...' : 'Delete Facility'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign In-House Radiologist Quick Action Dialog */}
      <Dialog
        open={assignRadiologistDialogOpen}
        onClose={() => setAssignRadiologistDialogOpen(false)}
        maxWidth={false}
        PaperProps={{
          sx: {
            width: { xs: '95vw', sm: '540px' },
            maxWidth: '560px !important',
            maxHeight: '88vh',
            borderRadius: '16px',
            bgcolor: cardBg,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          },
        }}
      >
        <DialogTitle sx={{ px: 2.5, py: 1.25, borderBottom: `1px solid ${borderColor}`, bgcolor: cardBg }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" alignItems="center" gap={1.5}>
              <Avatar sx={{ width: 34, height: 34, background: alpha(teslaColors.primary, 0.1), color: teslaColors.primary }}>
                <LocalHospital sx={{ fontSize: 20 }} />
              </Avatar>
              <Box>
                <Typography variant="subtitle1" fontWeight={700} sx={{ color: textColorPrimary, fontSize: '0.95rem' }}>
                  Assign In-House Radiologist
                </Typography>
                <Typography variant="body2" sx={{ color: textColorSecondary, fontSize: '0.78rem' }}>
                  {selectedFacility?.facilityName} ({selectedFacility?.facilityId})
                </Typography>
              </Box>
            </Box>
            <IconButton onClick={() => setAssignRadiologistDialogOpen(false)} size="small" sx={{ color: textColorSecondary }}>
              <Close sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 2.5, overflowY: 'auto' }}>
          <Box mb={2}>
            <Typography variant="body2" color="textSecondary" mb={2.5}>
              Assign a dedicated DycoVue in-house radiologist to this facility. The radiologist will automatically see all incoming orders and scans in their Reader Portal without the hospital needing to request each study manually.
            </Typography>

            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Designated In-House Radiologist</InputLabel>
              <Select
                value={selectedRadId}
                label="Designated In-House Radiologist"
                onChange={(e) => setSelectedRadId(e.target.value)}
                sx={{ borderRadius: '12px' }}
              >
                <MenuItem value="">
                  <em>None (Hospital uses on-site radiologist)</em>
                </MenuItem>
                {inHouseRadiologists
                  .filter((rad) => {
                    // If this radiologist is the currently assigned one for THIS facility, always show them
                    const currentAssignedId = selectedFacility?.inHouseRadiologist?._id || selectedFacility?.inHouseRadiologist;
                    if (rad._id === currentAssignedId) return true;
                    // Exclude radiologists whose primary facility is already this facility
                    const radFacilityId = rad.facility?._id || rad.facility;
                    const thisFacilityId = selectedFacility?._id;
                    return !radFacilityId || radFacilityId.toString() !== thisFacilityId?.toString();
                  })
                  .map((rad) => {
                    const currentAssignedId = selectedFacility?.inHouseRadiologist?._id || selectedFacility?.inHouseRadiologist;
                    const isCurrentlyAssigned = rad._id === currentAssignedId;
                    return (
                      <MenuItem key={rad._id} value={rad._id}>
                        Dr. {rad.firstName} {rad.lastName} ({rad.specialty || 'Radiology'}) — {rad.email}
                        {isCurrentlyAssigned && ' ✓ Currently assigned'}
                      </MenuItem>
                    );
                  })}
              </Select>
            </FormControl>

            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: '12px',
                borderColor: alpha(teslaColors.primary, 0.2),
                background: alpha(teslaColors.primary, 0.03),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Box>
                <Typography variant="subtitle2" fontWeight={600}>
                  Auto-Route Scans to In-House Reader
                </Typography>
                <Typography variant="caption" color="textSecondary" display="block">
                  Automatically route all incoming hospital scans and orders to this radiologist.
                </Typography>
              </Box>
              <Switch
                checked={autoAssignToggle}
                onChange={(e) => setAutoAssignToggle(e.target.checked)}
                color="primary"
              />
            </Paper>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1.25, borderTop: `1px solid ${borderColor}`, bgcolor: cardBg }}>
          <Button
            onClick={() => setAssignRadiologistDialogOpen(false)}
            sx={{ borderRadius: '8px' }}
          >
            Cancel
          </Button>
          <GradientButton
            onClick={handleSaveAssignRadiologist}
            disabled={assigningRadiologist}
            sx={{ borderRadius: '8px', px: 2.5 }}
          >
            {assigningRadiologist ? 'Saving...' : 'Save Assignment'}
          </GradientButton>
        </DialogActions>
      </Dialog>

      {/* DICOM/HL7 Integration Dialog */}
      <FacilityIntegrationDialog
        open={integrationDialogOpen}
        onClose={() => setIntegrationDialogOpen(false)}
        facility={selectedFacility}
      />
    </Box>
  );
}

export default FacilityManagement;